// FILE: src/pages/Clients/ClientDetail.jsx
import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { clientsAPI, visitsAPI, documentsAPI, carePlansAPI, medicaidAPI } from '../../api/services';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import ClientFormModal from './ClientFormModal';
import '../../components/UI/UI.css';
import './Clients.css';

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-US',{year:'numeric',month:'short',day:'numeric'}) : '—';
const fmtDT = (d) =>
  d ? new Date(d).toLocaleString('en-US',{month:'short',day:'numeric',hour:'2-digit',minute:'2-digit'}) : '—';

const TABS = ['Profile','Visits','Documents','Care Plans','Medicaid'];
const colors = ['#1a4d8c','#059669','#d97706','#7c3aed','#db2777','#0891b2'];

function Section({ title, children }) {
  return (
    <div className="card">
      <div style={{fontWeight:700,color:'var(--gray-700)',marginBottom:14,paddingBottom:10,borderBottom:'1px solid var(--gray-100)'}}>
        {title}
      </div>
      {children}
    </div>
  );
}

function InfoField({ label, value }) {
  return (
    <div className="info-field">
      <label>{label}</label>
      <span>{value}</span>
    </div>
  );
}

function VisitBadge({ status }) {
  const map = {'Completed':'badge-success','In Progress':'badge-warning','Scheduled':'badge-info','Cancelled':'badge-danger'};
  return <span className={`badge ${map[status]||'badge-gray'}`}>{status}</span>;
}

export default function ClientDetail() {
  const { id }      = useParams();
  const navigate    = useNavigate();
  const { hasRole } = useAuth();
  const canEdit     = hasRole('Admin','CaseManager');

  const [client,   setClient]   = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [tab,      setTab]      = useState(0);
  const [showEdit, setShowEdit] = useState(false);
  const [visits,   setVisits]   = useState([]);
  const [docs,     setDocs]     = useState([]);
  const [plans,    setPlans]    = useState([]);
  const [medicaid, setMedicaid] = useState([]);
  const [subLoad,  setSubLoad]  = useState(false);

  const loadClient = useCallback(async () => {
    try {
      setLoading(true);
      const res = await clientsAPI.getById(id);
      setClient(res.data.data);
    } catch {
      toast.error('Could not load client.');
      navigate('/clients');
    } finally { setLoading(false); }
  }, [id, navigate]);

  useEffect(() => { loadClient(); }, [loadClient]);

  useEffect(() => {
    if (!client) return;
    const load = async () => {
      setSubLoad(true);
      try {
        if (tab === 1) { const r = await visitsAPI.getByClient(id);   setVisits(r.data.data || []); }
        if (tab === 2) { const r = await documentsAPI.getByClient(id); setDocs(r.data.data || []); }
        if (tab === 3) { const r = await carePlansAPI.getByClient(id); setPlans(r.data.data || []); }
        if (tab === 4) { const r = await medicaidAPI.getByClient(id);  setMedicaid(r.data.data || []); }
      } catch { /* silent */ }
      finally { setSubLoad(false); }
    };
    load();
  // eslint-disable-next-line
  }, [tab, client]);

  if (loading) return <div className="loading-state">⏳ Loading client profile…</div>;
  if (!client) return null;

  const avatarColor = colors[client.ID % colors.length];
  const initials = `${client.FirstName?.[0]||''}${client.LastName?.[0]||''}`.toUpperCase();

  return (
    <div>
      <button className="btn btn-secondary btn-sm" style={{marginBottom:18}} onClick={() => navigate('/clients')}>
        ← Back to Clients
      </button>

      <div className="card" style={{marginBottom:18}}>
        <div className="client-detail-header">
          <div className="client-detail-avatar" style={{background:avatarColor}}>{initials}</div>
          <div style={{flex:1}}>
            <h2 style={{fontSize:'1.4rem',fontWeight:700,color:'var(--gray-800)'}}>
              {client.FirstName} {client.MiddleName ? client.MiddleName+' ':''}{client.LastName}
            </h2>
            <div style={{display:'flex',gap:10,marginTop:6,flexWrap:'wrap'}}>
              {client.MedicaidID && <span className="badge badge-success">Medicaid: {client.MedicaidID}</span>}
              {client.MedicareID && <span className="badge badge-info">Medicare: {client.MedicareID}</span>}
              {client.ServiceType && <span className="badge badge-primary">{client.ServiceType}</span>}
            </div>
          </div>
          {canEdit && (
            <button className="btn btn-secondary" onClick={() => setShowEdit(true)}>✏️ Edit Client</button>
          )}
        </div>
        <div style={{display:'flex',gap:28,flexWrap:'wrap',paddingTop:14,borderTop:'1px solid var(--gray-100)'}}>
          {[
            {label:'Date of Birth', value:fmtDate(client.DateOfBirth)},
            {label:'Phone',         value:client.CellNumber||client.PhoneNumber||'—'},
            {label:'Email',         value:client.Email||'—'},
            {label:'Coordinator',   value:client.Coordinator||'—'},
            {label:'Start Date',    value:fmtDate(client.RequestedStartDate)},
          ].map(f => (
            <div key={f.label}>
              <div style={{fontSize:'0.7rem',fontWeight:700,color:'var(--gray-400)',textTransform:'uppercase',letterSpacing:'0.5px'}}>{f.label}</div>
              <div style={{fontSize:'0.88rem',color:'var(--gray-700)',fontWeight:500,marginTop:2}}>{f.value}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="client-detail-tabs">
        {TABS.map((t,i) => (
          <button key={t} className={`client-detail-tab${tab===i?' active':''}`} onClick={() => setTab(i)}>{t}</button>
        ))}
      </div>

      {subLoad ? (
        <div className="loading-state">⏳ Loading…</div>
      ) : (
        <>
          {tab === 0 && (
            <div style={{display:'flex',flexDirection:'column',gap:18}}>
              <Section title="Personal Information">
                <div className="info-grid">
                  {[{label:'Gender',value:client.Gender||'—'},{label:'Ethnicity',value:client.Ethnicity||'—'},{label:'Language',value:client.Language||'—'}].map(f=><InfoField key={f.label} {...f}/>)}
                </div>
              </Section>
              <Section title="Contact Information">
                <div className="info-grid">
                  {[
                    {label:'Address',value:client.Address||'—'},{label:'City',value:client.City||'—'},
                    {label:'State / Zip',value:`${client.State||''} ${client.ZipCode||''}`.trim()||'—'},
                    {label:'Phone',value:client.PhoneNumber||'—'},{label:'Cell',value:client.CellNumber||'—'},
                    {label:'Email',value:client.Email||'—'},{label:'Primary Contact',value:client.PrimaryContact||'—'},
                    {label:'Emergency Contact',value:client.EmergencyContact||'—'},
                    {label:'Emergency Phone',value:client.EmergencyContactPhone||'—'},
                  ].map(f=><InfoField key={f.label} {...f}/>)}
                </div>
              </Section>
              <Section title="Insurance">
                <div className="info-grid">
                  {[
                    {label:'Medicaid ID',value:client.MedicaidID||'—'},{label:'Medicare ID',value:client.MedicareID||'—'},
                    {label:'Insurance Provider',value:client.InsuranceProvider||'—'},
                    {label:'Policy Number',value:client.PolicyNumber||'—'},
                    {label:'Renewal Date',value:fmtDate(client.InsuranceRenewalDate)},
                  ].map(f=><InfoField key={f.label} {...f}/>)}
                </div>
              </Section>
            </div>
          )}

          {tab === 1 && (
            <div className="card" style={{padding:0}}>
              {visits.length === 0 ? (
                <div className="empty-state"><div className="empty-state-icon">📅</div><div className="empty-state-text">No visits recorded yet.</div></div>
              ) : (
                <div className="table-wrap">
                  <table className="ec-table">
                    <thead><tr><th>Caregiver</th><th>Service</th><th>Start</th><th>End</th><th>Status</th><th>Notes</th></tr></thead>
                    <tbody>
                      {visits.map(v => (
                        <tr key={v.ID}>
                          <td className="bold">{v.CaregiverName||'—'}</td>
                          <td>{v.ServiceName||'—'}</td>
                          <td style={{fontSize:'0.8rem'}}>{fmtDT(v.StartTime)}</td>
                          <td style={{fontSize:'0.8rem'}}>{v.EndTime?fmtDT(v.EndTime):'—'}</td>
                          <td><VisitBadge status={v.VisitStatus}/></td>
                          <td style={{fontSize:'0.78rem',color:'var(--gray-500)',maxWidth:180}}>{v.Notes||'—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {tab === 2 && (
            <div className="card" style={{padding:0}}>
              {docs.length === 0 ? (
                <div className="empty-state"><div className="empty-state-icon">📄</div><div className="empty-state-text">No documents uploaded yet.</div></div>
              ) : (
                <div className="table-wrap">
                  <table className="ec-table">
                    <thead><tr><th>Document</th><th>Type</th><th>Uploaded</th><th>Expiry</th><th>View</th></tr></thead>
                    <tbody>
                      {docs.map(d => (
                        <tr key={d.ID}>
                          <td className="bold">{d.DocumentName}</td>
                          <td><span className="badge badge-gray">{d.DocumentType||'—'}</span></td>
                          <td style={{fontSize:'0.8rem'}}>{fmtDate(d.UploadDate)}</td>
                          <td style={{fontSize:'0.8rem'}}>{fmtDate(d.ExpirationDate)}</td>
                          <td>{d.FilePath && <a href={`http://localhost:5000/${d.FilePath}`} target="_blank" rel="noreferrer" className="btn btn-sm btn-secondary">📎 View</a>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {tab === 3 && (
            <div style={{display:'flex',flexDirection:'column',gap:14}}>
              {plans.length === 0 ? (
                <div className="card"><div className="empty-state"><div className="empty-state-icon">📋</div><div className="empty-state-text">No care plans yet.</div></div></div>
              ) : plans.map(p => (
                <div className="card" key={p.ID}>
                  <div style={{display:'flex',justifyContent:'space-between',marginBottom:10}}>
                    <div>
                      <div style={{fontWeight:700,color:'var(--gray-800)'}}>{p.PlanTitle||'Care Plan'}</div>
                      <div style={{fontSize:'0.78rem',color:'var(--gray-400)'}}>{fmtDate(p.StartDate)} – {p.EndDate?fmtDate(p.EndDate):'Ongoing'}</div>
                    </div>
                    <span className={`badge ${p.IsActive?'badge-success':'badge-gray'}`}>{p.IsActive?'Active':'Inactive'}</span>
                  </div>
                  {p.Goals && <div style={{fontSize:'0.85rem',color:'var(--gray-600)',lineHeight:1.6}}><strong>Goals:</strong> {p.Goals}</div>}
                  {p.Notes && <div style={{fontSize:'0.85rem',color:'var(--gray-500)',marginTop:6}}><strong>Notes:</strong> {p.Notes}</div>}
                </div>
              ))}
            </div>
          )}

          {tab === 4 && (
            <div className="card" style={{padding:0}}>
              {medicaid.length === 0 ? (
                <div className="empty-state"><div className="empty-state-icon">🏥</div><div className="empty-state-text">No Medicaid authorizations found.</div></div>
              ) : (
                <div className="table-wrap">
                  <table className="ec-table">
                    <thead><tr><th>Period</th><th>Auth #</th><th>Auth Hrs</th><th>Used Hrs</th><th>Remaining</th><th>Status</th></tr></thead>
                    <tbody>
                      {medicaid.map(m => {
                        const used=parseFloat(m.UsedHours||0), auth=parseFloat(m.AuthorizedHours||0);
                        const pct=auth>0?Math.min(100,Math.round((used/auth)*100)):0;
                        return (
                          <tr key={m.ID}>
                            <td style={{fontSize:'0.82rem'}}>{m.AuthMonth}/{m.AuthYear}</td>
                            <td style={{fontSize:'0.82rem'}}>{m.AuthorizationNumber||'—'}</td>
                            <td>{auth}h</td>
                            <td>{used}h</td>
                            <td>
                              <div style={{display:'flex',alignItems:'center',gap:8}}>
                                <div style={{width:80,height:6,background:'var(--gray-100)',borderRadius:99,overflow:'hidden'}}>
                                  <div style={{width:`${pct}%`,height:'100%',borderRadius:99,background:pct>=90?'var(--danger)':pct>=70?'var(--warning)':'var(--success)'}} />
                                </div>
                                <span style={{fontSize:'0.78rem',color:'var(--gray-500)'}}>{auth-used>0?`${(auth-used).toFixed(1)}h left`:'Exceeded'}</span>
                              </div>
                            </td>
                            <td><span className={`badge ${m.IsActive?'badge-success':'badge-gray'}`}>{m.IsActive?'Active':'Inactive'}</span></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {showEdit && (
        <ClientFormModal client={client} onClose={() => setShowEdit(false)}
          onSaved={() => { setShowEdit(false); loadClient(); }} />
      )}
    </div>
  );
}